import { faCirclePlus } from '@fortawesome/pro-light-svg-icons';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { useState, useEffect, useContext, useCallback, useMemo } from 'react';

import './ForYou.css';

import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation, useNavigate, Link } from 'react-router-dom';

import { pages } from '../assets/constants';
import { useFetchSingleMediaQuery } from '../contentTypes/mediaApiSlice';
import GoalSelector from '../goals/children/GoalSelector';
import { goalImages } from '../goals/goalImages';
import { useFetchSelectedGoalsQuery } from '../goals/goalsApiSlice';
import Nps from '../platform/components/Nps';
import { useUpdateNpsMutation } from '../profile/userApiSlice';
import { updateNpsState } from '../profile/userSlice';
import { Button } from '../shared/components/button/Button';
import { Slider } from '../shared/components/slider/Slider';
import LoadingSpinner from '../shared/components/UIElements/LoadingSpinner';
import { AuthContext } from '../shared/context/auth-context';
import { HeaderNavigation } from '../shared/headerNavigation/HeaderNavigation';
import { useFocusScroll } from '../shared/hooks/useFocusScroll.ts';
import { WelcomeAfterAssessmentModal } from '../shared/modal/WelcomeAfterAssessmentModal';
import { checkChallengeSecionId } from '../shared/util/challenges';
import {
  useFetchPageSlidersQuery,
  useFetchFeatureQuery,
  useFetchTakeActionQuery,
} from './mediaSlidersApiSlice';
import { setMediaSliders, setFeature, setTakeAction } from './mediaSlidersSlice';

function ForYou() {
  const auth = useContext(AuthContext);
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const pageid = window.location.pathname.split('/')[1];
  const section = pages.find((page) => page.url === pageid);
  const sectionid = section.id;
  const { data: selectedGoalsData } = useFetchSelectedGoalsQuery(auth.token, {
    skip: !auth.token,
  });

  const { data: featureData, isLoading: isFetchingFeature } = useFetchFeatureQuery(
    {
      token: auth.token,
      pageid: sectionid,
    },
    { skip: !sectionid || !auth.token }
  );

  const calculateLength = (lengthInMin) => {
    let length;

    if (lengthInMin < 60) {
      length = `${lengthInMin} ${t('FRY002')}`;
    } else if (lengthInMin % 60 === 0) {
      length = `${lengthInMin / 60} ${t('FRY003')}`;
    } else {
      length = `${Math.floor(lengthInMin / 60)} ${t('FRY003')} ${lengthInMin % 60} ${t('FRY002')} `;
    }
    return length;
  };

  const { data: takeActionData, isLoading: isFetchingTakeAction } = useFetchTakeActionQuery(
    {
      token: auth.token,
      pageid: sectionid,
    },
    { skip: !sectionid || !auth.token }
  );

  const { data: mediaSliders, isLoading: isFetchingSliders } = useFetchPageSlidersQuery(
    {
      token: auth.token,
      pageid: sectionid,
    },
    { skip: !sectionid || !auth.token }
  );

  const pageTitle = useSelector((state) => state.mediaSliders.title);
  const sliders = useSelector((state) => state.mediaSliders.sliders);
  const feature = useSelector((state) => state.mediaSliders.feature);
  const takeAction = useSelector((state) => state.mediaSliders.takeAction);
  const promotions = useSelector((state) => state.mediaSliders.promotions);
  const favourites = useSelector((state) => state.mediaSliders.favourites);
  const userData = useSelector((state) => state.user.userData);

  const selectedGoals = selectedGoalsData?.selectedGoals ?? [];
  const hasSelectedGoals = selectedGoals.length > 0;

  useEffect(() => {
    if (!isFetchingFeature && featureData) {
      dispatch(
        setFeature({
          feature: featureData.feature,
          promotions: featureData.promotions,
          sectionid,
        })
      );
    }
    if (!isFetchingTakeAction && takeActionData) {
      dispatch(setTakeAction(takeActionData));
    }
  }, [dispatch, featureData, isFetchingFeature, isFetchingTakeAction, takeActionData]);

  useEffect(() => {
    if (!isFetchingSliders && mediaSliders) {
      dispatch(setMediaSliders({ mediaSliders, sectionid }));
    }
  }, [dispatch, mediaSliders, isFetchingSliders]);

  useEffect(() => {
    const pageCont = document.getElementsByClassName('page-cont');
    if (
      pageCont.length > 0 &&
      !isFetchingFeature &&
      !isFetchingTakeAction &&
      sectionid === feature.type
    ) {
      pageCont[0].classList.add('visible');
    }
  }, [sectionid, isFetchingFeature, isFetchingTakeAction, feature?.type]);

  // nps
  const [npsOpen, setNpsOpen] = useState(false);
  const [updateNps] = useUpdateNpsMutation();

  const triggerNps = (action) => {
    if (action) {
      saveNps(action);
      setTimeout(function () {
        setNpsOpen(false);
      }, 495);
    } else {
      setNpsOpen(true);
    }
  };

  const saveNps = async (nps) => {
    try {
      const result = await updateNps({ token: auth.token, nps });
      if (result.isSuccess) {
        dispatch(updateNpsState(nps));
      }
    } catch (error) {
      console.error('Failed to update NPS:', error);
    }
  };

  // onboarding on to for you
  const [modalIsOpen, setModalIsOpen] = useState(false);
  const [openGoalSelector, setOpenGoalSelector] = useState(false);

  const referer = useLocation().search;

  useEffect(() => {
    if (referer && referer === '?referer=assessment') {
      setModalIsOpen(true);
    }
  }, [referer]);

  useFocusScroll();

  const handleCloseGoalSelector = () => {
    setOpenGoalSelector(false);
  };

  const modalClickHandler = useCallback(() => {
    setModalIsOpen(false);
  }, [modalIsOpen]);

  const [isClicked, setIsClicked] = useState(false);
  const {
    data: media,
    error,
    isLoading: isLoadingMedia,
  } = useFetchSingleMediaQuery(
    {
      token: auth?.token,
      url: `/${
        feature.media?.category?.toLowerCase() === 'programme'
          ? 'programme'
          : feature.media?.category?.toLowerCase() === 'masterclass'
            ? 'masterclass'
            : feature.media?.chid.startsWith('TA')
              ? 'take-actions'
              : 'media'
      }/${feature.media?.chid}`,
    },
    {
      skip: !isClicked,
    }
  );

  useEffect(() => {
    if (!isLoadingMedia && media && isClicked) {
      setIsClicked(false);
      let url = 'media';
      if (media.chid.startsWith('TA')) {
        url = 'take-actions';
      } else if (media.category?.toLowerCase() === 'programme') {
        url = 'programme';
      } else if (media.category?.toLowerCase() === 'masterclass') {
        url = 'masterclass';
      }
      navigate(`/${url}/${media.chid}/${media.title.replace(/ /g, '_')}`);
    }
  }, [media, isLoadingMedia, isClicked]);

  const heroUrl = useMemo(() => {
    if (!feature) return null;

    if (feature.media?.chid) {
      let url = 'media';
      if (feature.media.chid.startsWith('TA')) {
        url = 'take-actions';
      } else if (feature.media.category?.toLowerCase() === 'programme') {
        url = 'programme';
      } else if (feature.media.category?.toLowerCase() === 'masterclass') {
        url = 'masterclass';
      }
      return {
        url: `/${url}/${feature.media.chid}/${feature.media.title.replace(/ /g, '_')}`,
        external: false,
      };
    }
    if (feature.action_url && feature.action_url.endsWith('/challenge')) {
      const partsArray = feature.action_url.split('/');
      if (
        partsArray.length < 3 ||
        partsArray[partsArray.length - 1] !== 'challenge' ||
        checkChallengeSecionId(partsArray[partsArray.length - 2])
      ) {
        return null;
      }
      return {
        url: `/${partsArray[partsArray.length - 2]}/${partsArray[partsArray.length - 1]}`,
        external: false,
      };
    }
    if (feature.action_url) {
      return { url: feature.action_url, external: true };
    }
    return null;
  }, [feature]);

  return (
    <div id="main_container" className="foryou glowrm platform">
      {(isFetchingFeature || isFetchingSliders) && <LoadingSpinner asOverlay />}
      {modalIsOpen && <WelcomeAfterAssessmentModal onModalClose={modalClickHandler} />}
      {npsOpen && <Nps triggerNps={triggerNps} saveNps={saveNps} />}
      {openGoalSelector && <GoalSelector handleClose={handleCloseGoalSelector} />}
      <HeaderNavigation />
      <div className="page-cont" role="main">
        <div
          className="results-img"
          style={{
            background: `url(${feature.background_image}) center/cover no-repeat`,
          }}
        >
          <div className="gradient-overlay">
            <div className="hero-contents">
              <h1 className="hero-title">{feature.title}</h1>
              <h2>{feature.description}</h2>
              {feature.length && <p>{calculateLength(feature.length)}</p>}
              {heroUrl &&
                (heroUrl.external ? (
                  <a
                    href={heroUrl.url}
                    className="feature-link"
                    target="_blank"
                    rel="noopener noreferrer"
                    aria-label={`${feature.title} ${feature?.action_string?.toLowerCase()} ${t('external.link.label.newTab')}`}
                  >
                    {feature?.action_string?.toLowerCase()}
                  </a>
                ) : (
                  <Link
                    className="feature-link"
                    to={heroUrl.url}
                    aria-label={`${feature.title} ${feature?.action_string?.toLowerCase()}`}
                  >
                    {feature?.action_string?.toLowerCase()}
                  </Link>
                ))}
            </div>
          </div>
        </div>
        <div className="results-container">
          <div className="other-content">
            {promotions?.length > 0 && sectionid === pageTitle && (
              <Slider
                title={false}
                id="promo_dsakghsdalf"
                media={promotions}
                target={false}
                promo
              />
            )}
            {takeAction?.length > 0 && (
              <Slider title={t('FY005')} id="takeAction_kdasjf" media={takeAction} target={false} />
            )}
            {sectionid === 'foryou' && (
              <section className="your-goals-section" aria-labelledby="your-goals-title">
                <div className="your-goals-header">
                  <h2 className="slider_title" id="your-goals-title">
                    {t('G010')}
                  </h2>
                  <Button
                    variant="ghost"
                    onClick={() => setOpenGoalSelector(true)}
                    ariaLabel={t('G037')}
                  >
                    {t('G037')}
                  </Button>
                </div>
                {hasSelectedGoals ? (
                  <ul className="your-goals-list">
                    {selectedGoals.map((goalId) => {
                      return (
                        <li className="your-goals-pill your-goals-pill--selected" key={goalId}>
                          {goalImages[goalId] && (
                            <img
                              src={goalImages[goalId]}
                              alt=""
                              aria-hidden="true"
                              className="your-goals-pill-img"
                            />
                          )}
                          {t(`selectedGoals.goal_${goalId}`)}
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <div className="your-goals-empty">
                    {' '}
                    <FontAwesomeIcon
                      icon={faCirclePlus}
                      className="your-goals-pill-icon"
                      aria-hidden="true"
                    />{' '}
                    <p className="your-goals-empty-text">{t('forYou.goals.emptyState')}</p>
                  </div>
                )}
              </section>
            )}
            {sliders?.length > 0 &&
              sliders.map((slider) => {
                if (slider.media.length > 0) {
                  return (
                    <Slider
                      title={slider.title}
                      key={`${slider.id}+${favourites.media.length}`}
                      id={slider.id}
                      media={slider.media}
                      target={false}
                    />
                  );
                }
              })}
          </div>
        </div>
      </div>
    </div>
  );
}

export default ForYou;
